import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { appENV } from '@/utils/dom'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TicketDto, TicketKey } from 'api/Tickets/Types/TicketDto'
import { HeaderSortCell } from 'components/Filter'
import { WarningOutlined } from 'components/Icon'
import { Link } from 'components/Link'
import { formatLocalDateUTC, useDifferenceInFullDays } from 'utils/date'
import { isEnabledOnEnvs, links, ticketDetailsRouteUrl } from 'utils/featureToggles'
import { createFieldName } from 'utils/table/withExport'

import { isSalesTicketView } from './TicketsViewType'

export interface GridColumn extends GridColumnProps {
  fields?: string[]
  exportFn?(gridProps: GridCellProps): string
  show: boolean
  width?: number
}

export const ticketsColumn: GridColumn[] = [
  {
    title: 'Number',
    field: TicketKey.NUMBER,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: ticketDetailsRouteUrl(props.dataItem.id, props.dataItem.viewType.id),
          }}
        >
          {props.dataItem.number}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    sortable: false,
    width: 95,
    filter: 'text',
  },
  {
    title: 'Ticket Type',
    field: TicketKey.VIEWTYPE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Type',
    field: TicketKey.TYPE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Description',
    field: TicketKey.DESCRIPTION,
    cell: (props: GridCellProps) => (
      <td className='columns__content'>
        <div>
          {props.dataItem.description}{' '}
          {props.dataItem.exclamationMarkIcon && (
            <span style={{ color: 'red' }}>
              <WarningOutlined />
            </span>
          )}
        </div>
      </td>
    ),
    filterable: true,
    show: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Action',
    field: TicketKey.action,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TicketKey.CLIENT,
    cell: (props: GridCellProps) => {
      if (!props.dataItem.client) {
        return <td />
      }
      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: props.dataItem.client.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    filter: 'text',
  },
  {
    title: 'Country',
    field: createFieldName([TicketKey.COUNTRY, TicketKey.LEAD_COUNTRY]),
    fields: [TicketKey.COUNTRY, TicketKey.LEAD_COUNTRY],
    cell: (gridProps: GridCellProps) => {
      const data: TicketDto = gridProps.dataItem

      const clientCountry = data.client?.country
      const leadCountry = data.lead?.country

      if (isSalesTicketView(data?.viewType?.id)) {
        if (clientCountry?.name) {
          return <td>{clientCountry.name}</td>
        }
        if (leadCountry?.name) {
          return <td>{leadCountry.name}</td>
        }
      }

      if (clientCountry?.name) {
        return <td>{clientCountry.name}</td>
      }

      return <td />
    },
    exportFn: (gridProps: GridCellProps): string => {
      const data: TicketDto = gridProps.dataItem
      const clientCountry = data.client?.country
      const leadCountry = data.lead?.country

      if (isSalesTicketView(data?.viewType?.id)) {
        if (clientCountry?.name) {
          return clientCountry.name
        }
        if (leadCountry?.name) {
          return leadCountry.name
        }
      }

      if (clientCountry?.name) {
        return clientCountry.name
      }

      return ''
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Status',
    field: TicketKey.STATUS,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Assignee',
    field: TicketKey.ASSIGNEE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: createFieldName([TicketKey.TICKMILL_COMPANY, TicketKey.LEAD_TICKMILL_COMPANY]),
    fields: [TicketKey.TICKMILL_COMPANY, TicketKey.LEAD_TICKMILL_COMPANY],
    cell: (gridProps: GridCellProps) => {
      const data = gridProps.dataItem

      if (isSalesTicketView(data?.viewType?.id)) {
        const leadCompany = data.lead?.tickmillCompany
        if (leadCompany?.id && leadCompany?.name) {
          return (
            <td>
              <CompanyTag id={leadCompany.id}>{leadCompany.name}</CompanyTag>
            </td>
          )
        }

        return <td />
      }

      const clientCompany = data.client?.tickmillCompany
      if (clientCompany?.id) {
        return (
          <td>
            <CompanyTag id={clientCompany.id}>{clientCompany.name}</CompanyTag>
          </td>
        )
      }

      return <td />
    },
    exportFn: (gridProps: GridCellProps): string => {
      const data = gridProps.dataItem

      if (isSalesTicketView(data?.viewType?.id)) {
        const leadCompany = data.lead?.tickmillCompany
        if (leadCompany?.name) {
          return leadCompany.name
        }

        return ''
      }

      const clientCompany = data.client?.tickmillCompany
      if (clientCompany?.id) {
        return clientCompany.name
      }

      return ''
    },
    show: true,
    sortable: false,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TicketKey.DATE_CREATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.createDate, true)}
        <br />
        {useDifferenceInFullDays(props.dataItem.createDate)}
      </td>
    ),
    show: true,
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TicketKey.DATE_UPDATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {props.dataItem.updateDate && (
          <>
            {formatLocalDateUTC(props.dataItem.updateDate, true)}
            <br />
            {useDifferenceInFullDays(props.dataItem.updateDate)}
          </>
        )}
      </td>
    ),
    show: isEnabledOnEnvs([appENV.development, appENV.localhost]),
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
]
